import { useState, useRef, useEffect } from 'react';
import { Link2, Check, ChevronDown, Loader2 } from 'lucide-react';
import { api } from '../../api/client';

interface CopyLinksButtonProps {
  imageIds: string[];
  /** Etikett att visa när det är 0 bilder. Default: "No images". */
  emptyLabel?: string;
}

interface SignedUrlsResponse {
  urls: { id: string; url: string }[];
  size: string;
  ttlDays: number;
  expiresAt: string;
}

type Size = 'thumb' | 'preview' | 'full';

const SIZE_LABEL: Record<Size, string> = {
  thumb: 'Thumb (400px)',
  preview: 'Preview (1600px)',
  full: 'Full (original)',
};

/**
 * Knapp som hämtar signerade publika URL:er för en lista av bilder och
 * kopierar dem till clipboard som radseparerad text.
 *
 * Klick på chevron öppnar en liten meny för att välja storlek.
 * Klick på själva knappen kopierar med senast vald storlek (default: preview).
 */
export function CopyLinksButton({ imageIds, emptyLabel = '0 results' }: CopyLinksButtonProps) {
  const [size, setSize] = useState<Size>('preview');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Stäng menyn på klick utanför
  useEffect(() => {
    if (!open) return;
    function onDoc(e: globalThis.MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Reset success-state efter 2s
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 2000);
    return () => clearTimeout(t);
  }, [done]);

  async function copyLinks(chosenSize: Size) {
    if (imageIds.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const data = await api.post<SignedUrlsResponse>('/images/signed-urls', {
        imageIds,
        size: chosenSize,
        ttlDays: 30,
      });
      // Bygg absoluta URL:er (origin + relativ path) så de fungerar när
      // användaren klistrar in dem i en LLM eller chatt.
      const origin = window.location.origin;
      const text = data.urls.map((u) => `${origin}${u.url}`).join('\n');
      await navigator.clipboard.writeText(text);
      setDone(true);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte kopiera');
    } finally {
      setBusy(false);
    }
  }

  const isEmpty = imageIds.length === 0;
  const label = isEmpty
    ? emptyLabel
    : done
    ? `Copied ${imageIds.length}!`
    : `Copy ${imageIds.length} link${imageIds.length === 1 ? '' : 's'}`;

  return (
    <div ref={menuRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => copyLinks(size)}
        disabled={isEmpty || busy}
        title={isEmpty ? 'Filtrera fram bilder först' : `Kopiera ${SIZE_LABEL[size]} URL:er till clipboard`}
        className={[
          'flex items-center gap-2 pl-3.5 pr-2.5 py-2 text-xs min-h-[36px] rounded-l-lg transition-colors border border-r-0',
          isEmpty
            ? 'opacity-40 cursor-not-allowed text-loupe-text-3 bg-loupe-bg-2 border-loupe-border'
            : done
            ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
            : 'text-loupe-text-2 bg-loupe-bg-2 border-loupe-border hover:text-loupe-text hover:bg-loupe-bg-3',
        ].join(' ')}
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : done ? (
          <Check className="w-3.5 h-3.5" />
        ) : (
          <Link2 className="w-3.5 h-3.5" />
        )}
        <span className="whitespace-nowrap">{label}</span>
      </button>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={isEmpty || busy}
        title="Välj bildstorlek för länkarna"
        aria-label="Välj storlek"
        className={[
          'flex items-center justify-center w-8 min-h-[36px] rounded-r-lg transition-colors border',
          isEmpty
            ? 'opacity-40 cursor-not-allowed text-loupe-text-3 bg-loupe-bg-2 border-loupe-border'
            : 'text-loupe-text-2 bg-loupe-bg-2 border-loupe-border hover:text-loupe-text hover:bg-loupe-bg-3',
        ].join(' ')}
      >
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className={[
            'absolute right-0 top-full mt-1.5 z-[60]',
            'bg-loupe-bg-2 border border-loupe-border rounded-lg shadow-xl',
            'min-w-[220px] py-1 overflow-hidden',
          ].join(' ')}
        >
          <div className="px-3 py-2 text-[10px] tracking-[0.15em] uppercase text-loupe-text-3 border-b border-loupe-border">
            Storlek på länkarna
          </div>
          {(['thumb', 'preview', 'full'] as Size[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setSize(s);
                copyLinks(s);
              }}
              className={[
                'w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between gap-2',
                'hover:bg-loupe-bg-3',
                size === s ? 'text-loupe-text' : 'text-loupe-text-2',
              ].join(' ')}
            >
              <span>{SIZE_LABEL[s]}</span>
              {size === s ? <Check className="w-3 h-3 text-loupe-text-3" /> : null}
            </button>
          ))}
          <div className="px-3 py-2 text-[10px] text-loupe-text-3 border-t border-loupe-border">
            Länkarna gäller i 30 dagar.
          </div>
        </div>
      )}

      {error ? (
        <div className="absolute right-0 top-full mt-1.5 px-3 py-1.5 bg-red-500/15 border border-red-500/30 rounded-md text-[11px] text-red-300 whitespace-nowrap z-[70]">
          {error}
        </div>
      ) : null}
    </div>
  );
}
